import React, { useState, useMemo, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHero from '../components/PageHero';
import FinalCTA from '../components/FinalCTA';
import Seo, { articleSchema } from '../components/Seo';
import { ArrowRight, User, Calendar, Search, Tag } from 'lucide-react';
import { BLOG_POSTS } from '../mock';
import { supabase, POSTS_TABLE } from '../lib/supabase';
import { renderMarkdown } from '../lib/markdown';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return ''; }
};

// Map a Supabase row into the display shape used by cards & detail views.
const fromRow = (r) => ({
  slug: r.slug,
  title: r.title,
  excerpt: r.excerpt || '',
  cover: r.cover_url || 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&w=1200&q=80',
  author: 'Chesapeake Counseling Center',
  date: fmtDate(r.published_at || r.created_at || r.updated_at),
  tags: r.tags || [],
  content: r.content || '',
  body: (r.content || '').split(/\n{2,}/).filter(Boolean),
  _source: 'supabase',
});

const fromMock = (p) => ({ ...p, _source: 'mock' });

export function Blog() {
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState('All');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data, error } = await supabase
        .from(POSTS_TABLE)
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false });
      if (!mounted) return;
      const remote = !error && data ? data.map(fromRow) : [];
      // Merge: Supabase posts first, then any hardcoded mock posts NOT overridden by a Supabase slug.
      const remoteSlugs = new Set(remote.map((p) => p.slug));
      const mocks = BLOG_POSTS.filter((m) => !remoteSlugs.has(m.slug)).map(fromMock);
      setPosts([...remote, ...mocks]);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  const allTags = useMemo(() => {
    const set = new Set();
    posts.forEach(p => (p.tags || []).forEach(t => set.add(t)));
    return ['All', ...Array.from(set).sort()];
  }, [posts]);

  const filtered = posts.filter(p => {
    const matchesQuery = !query || (p.title + ' ' + p.excerpt).toLowerCase().includes(query.toLowerCase());
    const matchesTag = activeTag === 'All' || (p.tags || []).includes(activeTag);
    return matchesQuery && matchesTag;
  });

  return (
    <>
      <Seo
        title="Blog & Resources"
        description="Thoughts, tools, and reflections on trauma, anxiety, EMDR, relationships, and emotional wellness from the therapists at Chesapeake Counseling Center."
        path="/blog"
      />
      <PageHero
        eyebrow="Resources"
        title="Insights &"
        highlight="Stories"
        subtitle="Thoughts, tools, and reflections to support your emotional wellness and growth."
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Blog' }]}
      />

      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div className="relative w-full md:w-80">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--taupe-gray)]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-10 pr-4 py-2.5 rounded-full border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--blush-pink)]"
                style={{ borderColor: 'var(--soft-border)' }}
              />
            </div>
            <div className="text-sm text-[color:var(--taupe-gray)]">{loading ? 'Loading…' : `${filtered.length} article${filtered.length !== 1 ? 's' : ''}`}</div>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {allTags.map(t => (
              <button
                key={t}
                onClick={() => setActiveTag(t)}
                className={`text-xs px-3 py-1.5 rounded-full transition ${activeTag === t ? 'bg-[color:var(--dusty-rose)] text-white' : 'bg-white text-[color:var(--sage-dark)] hover:bg-[color:var(--cream)]'}`}
                style={activeTag !== t ? { border: '1px solid var(--soft-border)' } : {}}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="card-soft overflow-hidden group" style={{ transitionDelay: `${(i % 6) * 60}ms` }}>
                <div className="aspect-[16/10] overflow-hidden bg-[color:var(--cream)]">
                  <img src={p.cover} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-[color:var(--taupe-gray)] mb-2">
                    <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {p.author}</span>
                    {p.date && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {p.date}</span>}
                  </div>
                  <h3 className="font-serif-display text-lg text-[color:var(--deep-sage)] mb-2 leading-snug">{p.title}</h3>
                  <p className="text-sm text-[color:var(--taupe-gray)] leading-relaxed line-clamp-3">{p.excerpt}</p>
                  {p.tags && p.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {p.tags.slice(0, 2).map(t => (
                        <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: 'rgba(184,190,164,0.22)', color: 'var(--sage-dark)' }}>{t}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 inline-flex items-center gap-1.5 text-sm text-[color:var(--dusty-rose)] font-medium">Read more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" /></div>
                </div>
              </Link>
            ))}
          </div>

          {!loading && filtered.length === 0 && (
            <div className="text-center py-16 text-[color:var(--taupe-gray)]">No articles match your search.</div>
          )}
        </div>
      </section>
      <FinalCTA />
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      // Try Supabase first
      const { data, error } = await supabase
        .from(POSTS_TABLE)
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle();

      if (!mounted) return;

      let p = null;
      if (!error && data) {
        p = fromRow(data);
      } else {
        const m = BLOG_POSTS.find(x => x.slug === slug);
        if (m) p = fromMock(m);
      }

      if (!p) { setNotFound(true); setLoading(false); return; }
      setPost(p);

      // Related — fetch other published posts sharing tags
      const { data: allPublished } = await supabase
        .from(POSTS_TABLE)
        .select('slug, title, cover_url, tags')
        .eq('status', 'published')
        .neq('slug', slug)
        .limit(20);

      const remoteSlugs = new Set((allPublished || []).map(r => r.slug));
      const remoteRelated = (allPublished || [])
        .filter(r => (r.tags || []).some(t => (p.tags || []).includes(t)))
        .map(r => ({ slug: r.slug, title: r.title, cover: r.cover_url }));

      const mockRelated = BLOG_POSTS
        .filter(x => x.slug !== slug && !remoteSlugs.has(x.slug) && (x.tags || []).some(t => (p.tags || []).includes(t)))
        .map(x => ({ slug: x.slug, title: x.title, cover: x.cover }));

      setRelated([...remoteRelated, ...mockRelated].slice(0, 3));
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [slug]);

  if (loading) return <div className="p-24 text-center text-[color:var(--taupe-gray)]">Loading…</div>;
  if (notFound || !post) {
    return (
      <section className="section-pad text-center">
        <h1 className="font-serif-display text-3xl text-[color:var(--deep-sage)] mb-4">Post not found</h1>
        <Link to="/blog" className="btn-primary inline-flex">← Back to blog</Link>
      </section>
    );
  }

  const p = post;
  return (
    <>
      <Seo
        title={p.title}
        description={p.excerpt}
        path={`/blog/${p.slug}`}
        image={p.cover}
        type="article"
        schema={articleSchema(p)}
      />
      <PageHero
        eyebrow="Article"
        title={p.title}
        subtitle={`${p.author}${p.date ? ' \u2022 ' + p.date : ''}`}
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Blog', to: '/blog' }, { label: p.title }]}
      />
      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="card-soft overflow-hidden mb-8">
            <img src={p.cover} alt={p.title} className="w-full h-72 md:h-96 object-cover" />
          </div>

          {p.tags && p.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {p.tags.map(t => (
                <span key={t} className="text-xs px-3 py-1 rounded-full inline-flex items-center gap-1" style={{ background: 'rgba(232,165,169,0.15)', color: 'var(--dusty-rose)' }}>
                  <Tag className="w-3 h-3" /> {t}
                </span>
              ))}
            </div>
          )}

          {p._source === 'supabase' ? (
            <div
              className="prose-blog space-y-5 text-[color:var(--deep-sage)] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(p.content) }}
            />
          ) : (
            <div className="space-y-5 text-[color:var(--deep-sage)] leading-relaxed">
              {(p.body || []).map((para, i) => <p key={i} className={i === 0 ? 'text-lg' : ''}>{para}</p>)}
            </div>
          )}

          <div className="mt-10 pt-8 border-t" style={{ borderColor: 'var(--soft-border)' }}>
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-[color:var(--dusty-rose)]"><ArrowRight className="w-4 h-4 rotate-180" /> Back to all articles</Link>
          </div>

          {related.length > 0 && (
            <div className="mt-12">
              <h3 className="font-serif-display text-2xl text-[color:var(--deep-sage)] mb-5">Related Articles</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {related.map(r => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="card-soft overflow-hidden group">
                    <div className="aspect-[16/10] overflow-hidden">
                      {r.cover ? <img src={r.cover} alt={r.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /> : null}
                    </div>
                    <div className="p-4"><div className="font-serif-display text-sm text-[color:var(--deep-sage)] leading-snug">{r.title}</div></div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
